/**
 * components/project-members-panel.tsx
 *
 * Members tab on the project detail page.
 *
 * Shows who has access to this project and at what permission level.
 * Admin and project owners can add/remove/change members.
 * Other members can only view the list.
 *
 * Permission levels:
 *   owner  — created the project, can manage members
 *   editor — can edit assets, run assessments
 *   viewer — read-only
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { Modal } from './admin-ui';

interface ProjectMember {
  id: string;
  user_id: string;
  email: string;
  display_name: string;
  permission: 'owner' | 'editor' | 'viewer';
  granted_at: string;
  granted_by_name: string | null;
  status: 'Active' | 'Disabled';
}

interface UserOption {
  id: string;
  email: string;
  display_name: string;
}

const PERMISSION_LABELS: Record<string, string> = {
  owner:  'Owner',
  editor: 'Editor',
  viewer: 'Viewer',
};

const PERMISSION_DESC: Record<string, string> = {
  owner:  'Can edit, run assessments, manage members',
  editor: 'Can edit assets and run assessments',
  viewer: 'Read-only access to results and reports',
};

export function ProjectMembersPanel({ projectId }: { projectId: string }) {
  const { user, hasRole } = useAuth();
  const qc = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);

  const membersQ = useQuery<ProjectMember[]>({
    queryKey: ['project-members', projectId],
    queryFn: () => api.get<ProjectMember[]>(`/projects/${projectId}/members`),
  });

  const members = membersQ.data ?? [];

  // Determine current user's permission on this project
  const myMembership = members.find((m) => m.user_id === user?.id);
  const isAdmin = hasRole('admin');
  const isOwner = myMembership?.permission === 'owner';
  const canManage = isAdmin || isOwner;

  const removeMember = useMutation({
    mutationFn: (userId: string) =>
      api.delete(`/projects/${projectId}/members/${userId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['project-members', projectId] }),
  });

  const changePerm = useMutation({
    mutationFn: ({ userId, permission }: { userId: string; permission: string }) =>
      api.patch(`/projects/${projectId}/members/${userId}`, { permission }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['project-members', projectId] }),
  });

  if (membersQ.isLoading) {
    return <div className="p-8 text-sm text-ink-500">Loading members…</div>;
  }
  if (membersQ.isError) {
    return <div className="p-8 text-sm text-red-700">Failed to load members.</div>;
  }

  return (
    <div className="p-6 max-w-3xl">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-medium text-ink">Project access</h2>
          <p className="text-sm text-ink-500 mt-0.5">
            Only members listed here can see and work on this project.
          </p>
        </div>
        {canManage && (
          <button
            type="button"
            onClick={() => setShowAdd(true)}
            className="text-sm px-3 py-1.5 bg-accent text-white rounded hover:bg-accent-hover"
          >
            Add member
          </button>
        )}
      </div>

      <div className="border border-ink-200 rounded overflow-hidden">
        <table className="min-w-full text-sm">
          <thead className="bg-ink-50 text-xs text-ink-500 uppercase tracking-wide">
            <tr>
              <th className="px-4 py-2 text-left">User</th>
              <th className="px-4 py-2 text-left">Permission</th>
              <th className="px-4 py-2 text-left">Added</th>
              {canManage && <th className="px-4 py-2 text-right">Actions</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-100">
            {members.map((m) => (
              <tr key={m.user_id} className={m.status === 'Disabled' ? 'opacity-50' : ''}>
                <td className="px-4 py-3">
                  <div className="font-medium text-ink">{m.display_name}</div>
                  <div className="text-xs text-ink-500 font-mono">{m.email}</div>
                  {m.status === 'Disabled' && (
                    <span className="text-xs text-amber-700">Account disabled</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {canManage && m.permission !== 'owner' ? (
                    <select
                      value={m.permission}
                      onChange={(e) =>
                        changePerm.mutate({ userId: m.user_id, permission: e.target.value })
                      }
                      className="text-xs border border-ink-200 rounded px-2 py-1 focus:outline-none focus:border-accent"
                    >
                      <option value="editor">Editor</option>
                      <option value="viewer">Viewer</option>
                    </select>
                  ) : (
                    <div>
                      <span
                        className={`inline-block text-xs px-2 py-0.5 rounded ${
                          m.permission === 'owner'
                            ? 'bg-accent/10 text-accent'
                            : m.permission === 'editor'
                            ? 'bg-teal-50 text-teal-700'
                            : 'bg-ink-50 text-ink-600'
                        }`}
                      >
                        {PERMISSION_LABELS[m.permission]}
                      </span>
                      <div className="text-xs text-ink-400 mt-0.5">
                        {PERMISSION_DESC[m.permission]}
                      </div>
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 text-xs text-ink-500">
                  {new Date(m.granted_at).toLocaleDateString()}
                  {m.granted_by_name && (
                    <div className="text-ink-400">by {m.granted_by_name}</div>
                  )}
                </td>
                {canManage && (
                  <td className="px-4 py-3 text-right">
                    {m.user_id !== user?.id && m.permission !== 'owner' && (
                      <button
                        type="button"
                        onClick={() => removeMember.mutate(m.user_id)}
                        className="text-xs text-red-700 hover:underline"
                      >
                        Remove
                      </button>
                    )}
                    {m.user_id === user?.id && (
                      <span className="text-xs text-ink-400 italic">You</span>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showAdd && (
        <AddMemberModal
          projectId={projectId}
          existingUserIds={members.map((m) => m.user_id)}
          onClose={() => setShowAdd(false)}
          onAdded={() => {
            qc.invalidateQueries({ queryKey: ['project-members', projectId] });
            setShowAdd(false);
          }}
        />
      )}
    </div>
  );
}

function AddMemberModal({
  projectId,
  existingUserIds,
  onClose,
  onAdded,
}: {
  projectId: string;
  existingUserIds: string[];
  onClose: () => void;
  onAdded: () => void;
}) {
  const [selectedUserId, setSelectedUserId] = useState('');
  const [permission, setPermission] = useState<'editor' | 'viewer'>('viewer');
  const [error, setError] = useState<string | null>(null);

  // Users who could be added: served to project owners as well as administrators.
  const usersQ = useQuery<UserOption[]>({
    queryKey: ['member-candidates', projectId],
    queryFn: () => api.get<UserOption[]>(`/projects/${projectId}/member-candidates`),
    retry: false,
  });

  const allUsers = (usersQ.data ?? []).filter((u) => !existingUserIds.includes(u.id));

  const addMut = useMutation({
    mutationFn: () =>
      api.post(`/projects/${projectId}/members`, {
        user_id: selectedUserId,
        permission,
      }),
    onSuccess: () => onAdded(),
    onError: (e: unknown) => {
      if (e instanceof ApiError) setError(e.message);
      else setError('Failed to add member');
    },
  });

  return (
    <Modal open={true} title="Add project member" onClose={onClose}>
      <div className="space-y-4">
        <div>
          <label className="block text-sm text-ink-700 mb-1">Select user</label>
          {usersQ.isLoading ? (
            <p className="text-sm text-ink-500">Loading users…</p>
          ) : usersQ.isError ? (
            <p className="text-sm text-red-700" role="alert">{usersQ.error instanceof ApiError ? usersQ.error.message : 'Could not load users'}</p>
          ) : allUsers.length === 0 ? (
            <p className="text-sm text-ink-500">All users already have access to this project.</p>
          ) : (
            <select
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              className="w-full px-3 py-2 border border-ink-200 rounded text-sm focus:outline-none focus:border-accent"
            >
              <option value="">— choose a user —</option>
              {allUsers.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.display_name} ({u.email})
                </option>
              ))}
            </select>
          )}
        </div>

        <div>
          <label className="block text-sm text-ink-700 mb-1">Permission level</label>
          <div className="space-y-2">
            {(['editor', 'viewer'] as const).map((p) => (
              <label key={p} className="flex items-start gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="permission"
                  value={p}
                  checked={permission === p}
                  onChange={() => setPermission(p)}
                  className="mt-0.5"
                />
                <div>
                  <div className="text-sm font-medium text-ink">{PERMISSION_LABELS[p]}</div>
                  <div className="text-xs text-ink-500">{PERMISSION_DESC[p]}</div>
                </div>
              </label>
            ))}
          </div>
        </div>

        {error && <div className="text-sm text-red-700">{error}</div>}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-sm border border-ink-200 rounded hover:bg-ink-50"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!selectedUserId || addMut.isPending}
            onClick={() => addMut.mutate()}
            className="px-3 py-1.5 text-sm bg-accent text-white rounded hover:bg-accent-hover disabled:opacity-50"
          >
            {addMut.isPending ? 'Adding…' : 'Add member'}
          </button>
        </div>
      </div>
    </Modal>
  );
}